import React, { useEffect, useRef, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getLastSearchUrl } from '../utils/lastSearchUrl';
import { bookingSessionHeaders } from '../utils/bookingSessionCorrelation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function normalizeHttpDetail(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return null;
}

function numOrZero(v) {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * OTA-style fare change consent (Tripjack soft reprice).
 * Never auto-continues — user must click Accept.
 */
export default function FareRepriceModal({
  open,
  onClose,
  detail,
  priceIds,
  repriceAttempt = 0,
  onRefreshSuccess,
  source = 'review_booking',
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const [localAttempt, setLocalAttempt] = useState(() => numOrZero(repriceAttempt));
  const detailRef = useRef(detail);

  useEffect(() => {
    detailRef.current = detail;
  }, [detail]);

  const resetKey = `${(Array.isArray(priceIds) ? priceIds : []).join('|')}#${source || ''}`;

  useEffect(() => {
    if (!open) return;
    setLocalAttempt(numOrZero(repriceAttempt));
  }, [open, resetKey]);

  useEffect(() => {
    const n = numOrZero(repriceAttempt);
    setLocalAttempt((prev) => Math.max(prev, n));
  }, [repriceAttempt]);

  if (!open || !detail) return null;

  const oldF = detail.old_fare;
  const newF = detail.new_fare;
  const delta = detail.delta ?? (Number(newF) - Number(oldF));
  const action = detail.action;
  const exhausted = detail.code === 'FARE_REPRICE_EXHAUSTED';
  const backendAttempt = Number(detail.reprice_attempt ?? detail.repriceAttempt ?? 0);
  const showAgainDuringRefresh = backendAttempt > 0;

  const accept = async () => {
    if (!Array.isArray(priceIds) || !priceIds.length) {
      setLocalError('Missing price ids — please start a new search.');
      return;
    }
    setBusy(true);
    setLocalError('');
    try {
      const d = detailRef.current || detail;
      const o = Number(d?.old_fare);
      const n = Number(d?.new_fare);
      const del = Number(
        d?.delta != null && d?.delta !== ''
          ? d.delta
          : (Number.isFinite(n) && Number.isFinite(o) ? n - o : NaN),
      );
      const acceptance = {
        old_total: o,
        new_total: n,
        delta: del,
        source,
      };
      if (!Number.isFinite(acceptance.old_total) || !Number.isFinite(acceptance.new_total) || !Number.isFinite(acceptance.delta)) {
        setLocalError('Invalid fare amounts — please close and try again.');
        setBusy(false);
        return;
      }
      const detailAttempt = numOrZero(d?.reprice_attempt ?? d?.repriceAttempt);
      const attemptToSend = Math.max(numOrZero(localAttempt), detailAttempt);

      try {
        const resp = await axios.post(
          `${BACKEND_URL}/api/tripjack/reprice/refresh`,
          {
            priceIds,
            reprice_attempt: attemptToSend,
            acceptance,
          },
          { withCredentials: true, headers: { ...bookingSessionHeaders() } },
        );

        setLocalError('');
        if (onRefreshSuccess) {
          await onRefreshSuccess({ type: 'ok', payload: resp.data, acceptance });
        }
      } catch (err) {
        const status = err?.response?.status;
        const data = err?.response?.data || {};
        const d = normalizeHttpDetail(data?.detail) || normalizeHttpDetail(data);

        if (status === 409 && d?.code === 'FARE_CHANGED') {
          setLocalError('');
          setLocalAttempt((prev) => {
            const nextAttemptRaw = d?.reprice_attempt ?? d?.repriceAttempt;
            const nextAttempt = Number(nextAttemptRaw);
            const resolved = !Number.isNaN(nextAttempt)
              ? Math.max(prev, attemptToSend + 1, nextAttempt)
              : Math.max(prev, attemptToSend + 1);

            return resolved;
          });
          if (onRefreshSuccess) {
            await onRefreshSuccess({ type: 'still_changed', detail: d, acceptance });
          }
          return;
        }

        if (status === 409 && d?.code === 'FARE_REPRICE_EXHAUSTED') {
          setLocalError('');
          const ra = d?.reprice_attempt ?? d?.repriceAttempt;
          const n = Number(ra);
          if (!Number.isNaN(n)) {
            setLocalAttempt((prev) => Math.max(prev, n));
          }
          if (onRefreshSuccess) {
            await onRefreshSuccess({ type: 'exhausted', detail: d });
          }
          return;
        }

        if (
          d?.action === 'rebook'
          || d?.blocking === true
          || [808, 1000, 1009, 1071].includes(Number(d?.errCode))
        ) {
          setLocalError('');
          if (onRefreshSuccess) {
            await onRefreshSuccess({
              type: 'exhausted',
              detail: {
                code: 'FARE_REPRICE_EXHAUSTED',
                action: 'HARD_RESET',
                message: d.user_message || d.message || 'This fare has expired. Please search again.',
                errCode: d.errCode,
              },
            });
          }
          return;
        }

        setLocalError(
          d?.user_message
          || d?.message
          || (typeof data?.detail === 'string' ? data.detail : null)
          || err?.message
          || 'Refresh failed',
        );
      }
    } catch (e) {
      setLocalError(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4" data-testid="fare-reprice-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        {/* On HARD_RESET / exhausted, redirect to search rather than allowing
            a soft dismiss that would leave the user with a stale priceId. */}
        <button
          type="button"
          onClick={() => {
            if (exhausted || action === 'HARD_RESET') {
              onClose?.();
              navigate(getLastSearchUrl('/search'));
              return;
            }
            onClose?.();
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 text-amber-700 mb-3">
          <AlertTriangle className="w-6 h-6 shrink-0" />
          <h2 className="text-lg font-bold text-gray-900">Fare updated</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          The airline/supplier updated the fare before ticketing.
        </p>
        {showAgainDuringRefresh ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 mb-3">
            Fare changed again. Please review the updated fare.
          </div>
        ) : null}
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm space-y-2 mb-4">
          <div className="flex justify-between">
            <span className="text-gray-500">Old fare</span>
            <span className="font-semibold">₹{Number(oldF || 0).toLocaleString('en-IN')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">New fare</span>
            <span className="font-semibold">₹{Number(newF || 0).toLocaleString('en-IN')}</span>
          </div>
          <div className="flex justify-between border-t border-gray-200 pt-2">
            <span className="text-gray-500">Difference</span>
            <span className={`font-bold ${Number(delta) > 0 ? 'text-red-600' : 'text-emerald-700'}`}>
              {Number(delta) > 0 ? '+' : ''}₹{Number(delta || 0).toLocaleString('en-IN')}
            </span>
          </div>
        </div>
        {action === 'HARD_RESET' || exhausted ? (
          <p className="text-sm text-red-700 mb-4">
            {detail.message || 'The fare changed too many times. Please start a new search for this itinerary.'}
          </p>
        ) : null}
        {localError ? (
          <p className="text-sm text-red-600 mb-3">{localError}</p>
        ) : null}
        <div className="flex flex-col gap-3">
          {/* On exhausted/HARD_RESET, hide the redundant Cancel row entirely —
              the prominent "Search again" CTA below is the only safe path
              forward. Lets us avoid the dead-end "user closes, retries, gets
              FARE_UNAVAILABLE" loop. */}
          {!exhausted && action !== 'HARD_RESET' && (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-800 hover:bg-gray-50 font-medium"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={accept}
                disabled={busy}
                className="flex-1 py-2.5 rounded-lg bg-[#659A9B] text-white font-semibold hover:bg-[#5a8b8c] disabled:opacity-60"
                data-testid="fare-reprice-accept"
              >
                {busy ? 'Refreshing…' : 'Accept updated fare'}
              </button>
            </div>
          )}
          {exhausted ? (
            <button
              type="button"
              onClick={() => {
                onClose?.();
                navigate(getLastSearchUrl('/search'));
              }}
              className="w-full py-2.5 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800"
              data-testid="fare-reprice-search-again"
            >
              Search again
            </button>
          ) : null}
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Your passenger and contact details are kept. Seat selections may need reconfirmation if the supplier session changes.
        </p>
      </div>
    </div>
  );
}
